import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Users, Check } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { toast } from "sonner";

const BulkPartnerModal = ({ cars, token, onClose, onDone }) => {
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState("");
  const [monthly, setMonthly] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch("/flotila/partners", { token }).then(setPartners).catch((e) => toast.error(e.message));
  }, [token]);

  const list = cars.filter((c) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [c.brand, c.model, c.license_plate, c.partner_name].some((x) => (x || "").toLowerCase().includes(s));
  });
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allVisible = list.length > 0 && list.every((c) => sel.has(c.id));
  const toggleAll = () => setSel((p) => {
    const n = new Set(p);
    if (allVisible) list.forEach((c) => n.delete(c.id));
    else list.forEach((c) => n.add(c.id));
    return n;
  });

  const unassign = partnerId === "__none__";
  const monthlyNet = monthly ? Math.round(Number(monthly) / 1.21).toLocaleString("cs-CZ") : null;

  const apply = async () => {
    if (!partnerId) { toast.error("Vyberte partnera (nebo EffectDeal pro zrušení)."); return; }
    if (sel.size === 0) { toast.error("Vyberte alespoň jeden vůz."); return; }
    setSaving(true);
    try {
      const r = await apiFetch("/flotila/cars/bulk-partner", {
        method: "POST", token,
        body: {
          car_ids: [...sel],
          partner_id: unassign ? null : partnerId,
          partner_monthly: unassign || monthly === "" ? null : Number(monthly),
        },
      });
      toast.success(`Hotovo: upraveno ${r.updated} vozů.`);
      onDone();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="bulk-partner-modal">
      <div className="fl-card rounded-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 fl-divider">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold inline-flex items-center gap-2"><Users className="w-5 h-5 fl-accent-text" /> Hromadné přiřazení partnera</h3>
          <button onClick={onClose} className="fl-muted hover:opacity-80" data-testid="bulk-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-3 fl-divider">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Partner (majitel)</span>
              <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className="fl-input w-full" data-testid="bulk-partner-select">
                <option value="" className="bg-[#0a0a0a]">— vyberte —</option>
                <option value="__none__" className="bg-[#0a0a0a]">— EffectDeal (zrušit partnera) —</option>
                {partners.map((p) => <option key={p.id} value={p.id} className="bg-[#0a0a0a]">{p.name}{p.company ? ` (${p.company})` : ""}</option>)}
              </select>
            </label>
            <label className={`block ${unassign ? "opacity-40 pointer-events-none" : ""}`}><span className="text-[11px] uppercase tracking-wider fl-muted block mb-1">Výplata partnerovi (Kč/měs, s DPH)</span>
              <input type="number" value={monthly} onChange={(e) => setMonthly(e.target.value)} className="fl-input w-full" placeholder="např. 18000" data-testid="bulk-monthly" />
              {monthlyNet && <span className="block text-[10px] fl-muted mt-1">= {monthlyNet} Kč bez DPH (21 %)</span>}
            </label>
          </div>
        </div>

        <div className="px-5 py-3 flex items-center gap-3 fl-divider">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat vůz, SPZ…" className="fl-input flex-1" data-testid="bulk-search" />
          <button onClick={toggleAll} className="text-xs px-3 py-2 rounded-full fl-border border fl-muted hover:opacity-80 whitespace-nowrap" data-testid="bulk-select-all">{allVisible ? "Zrušit výběr" : "Vybrat vše"}</button>
          <span className="text-xs fl-muted whitespace-nowrap" data-testid="bulk-sel-count">{sel.size} vybráno</span>
        </div>

        <div className="overflow-y-auto flex-1 p-3 space-y-1">
          {list.map((c) => {
            const on = sel.has(c.id);
            return (
              <button key={c.id} onClick={() => toggle(c.id)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-colors ${on ? "fl-accent-soft fl-accent-border border" : "fl-row border border-transparent"}`} data-testid={`bulk-car-${c.id}`}>
                <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${on ? "fl-accent-bg" : "fl-border border"}`}>{on && <Check className="w-3.5 h-3.5" />}</span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate fl-text-2">{c.brand} {c.model}</span>
                  <span className="block text-[11px] fl-muted font-mono">{c.license_plate || "bez SPZ"}</span>
                </span>
                <span className="text-[11px] fl-muted shrink-0">{c.partner_name || "EffectDeal"}</span>
              </button>
            );
          })}
        </div>

        <div className="px-5 py-4 flex items-center justify-end gap-3 fl-divider">
          <button onClick={onClose} className="text-sm fl-muted hover:opacity-80" data-testid="bulk-cancel">Zrušit</button>
          <button onClick={apply} disabled={saving} className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full fl-accent-bg font-semibold disabled:opacity-60" data-testid="bulk-apply">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Přiřadit ({sel.size})</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkPartnerModal;
